import { useEffect, useState } from 'react';
import { Box } from '@/components/Box/Box';
import { DetailsRow } from '@/components/DetailsWidget/DetailsRow/DetailsRow';
import { OptionType, Select } from '@/components/form/fields/Select/Select';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { Button } from '@/components/Button/Button';
import { ListCardWrapper } from '@/components/Table/MobileTable/ui/styles';
import { TableConfigItemsListType } from '../types/types';
import { SortingButton } from './ui/SortingButton';
import noop from 'lodash/noop';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';

type MobileTableType<T> = {
  config: TableConfigItemsListType<T>;
  items: T[];
  isLoading: boolean;
  onRowClick?: (item: T) => void;
};

const getSortingOptions = <T,>(options: TableConfigItemsListType<T>): OptionType[] => {
  return options
    .filter((option) => option.hasSort)
    .map(({ name, header }) => {
      return {
        value: name,
        label: header,
      };
    });
};

export const MobileTable = <T extends { id: string }>({
  items = [],
  isLoading,
  onRowClick = noop,
  config = [],
}: MobileTableType<T>) => {
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
  const { ordering } = useGetSearchParams();
  const { replaceSearchParams } = useReplaceSearchParams();

  const hasItems = !!items.length && !isLoading;
  const sortingOptions = getSortingOptions<T>(config);

  useEffect(() => {
    if (ordering) {
      // Match ordering search param with options value removing "-" from the param
      setSelectedOption(sortingOptions.find((option) => option.value.includes(ordering.replace(/^-/, ''))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSorting = () => {
    replaceSearchParams({ ordering: null });
    setSelectedOption(null);
  };

  const changeSortingField = (option: OptionType) => {
    setSelectedOption(option);
    replaceSearchParams({ ordering: null });
  };

  return (
    <Box display='flex' flexDirection='column' rowGap='12px'>
      <Box display='flex' flexDirection='column' marginBottom='16px'>
        <Box display='flex' flexDirection='row' columnGap='8px'>
          <Select
            value={selectedOption}
            placeholder='Select sorting field'
            options={sortingOptions}
            onChange={changeSortingField}
          />
          <SortingButton option={selectedOption} />
        </Box>

        <Button mobileStretch variant='secondary' onClick={resetSorting}>
          Reset sorting
        </Button>
      </Box>

      {hasItems &&
        items.map((item: T) => {
          return (
            <ListCardWrapper key={item.id} onClick={() => onRowClick(item)}>
              {config.map(({ key, getValue, header, tooltip }) => {
                const { value, title } = getValue(item);

                return (
                  <DetailsRow
                    key={key}
                    title={title}
                    name={
                      <Box display='flex' flexDirection='row' alignItems='center' columnGap='8px'>
                        {header}
                        {tooltip}
                      </Box>
                    }
                  >
                    {value}
                  </DetailsRow>
                );
              })}
            </ListCardWrapper>
          );
        })}
    </Box>
  );
};
